import { PostEditorPostError, PostEditorPostErrors, PostGroupsType } from '../../../models/post-editor';
import { ErrorMessageSeverity } from '../../../constants/form';
import { PostEditorValidationMessages } from '../constants/validation-messages';
import { serializeText } from '../TextEditor/helpers/serializer';
import { severityResolver } from './helpers/severity-resolver';
import { PostTiming } from '../../../models/graphql';

type SamePostTimerObject = {
  [key in PostTiming]: number;
};
type SameVariationsObjectValue = { value: number; postIndex: number[] };
type SameVariationsObject = {
  [x: string]: SameVariationsObjectValue;
};

type DateTimeTimings = {
  dateTime: string;
  postIndex: number;
};

type TextVariations = {
  text: string;
  postIndex: number;
};

// const MaxPnProfilesCount = 3;

const isPostTimingKeyGuard = (key: unknown): key is keyof typeof PostTiming =>
  typeof key === 'string' && key in PostTiming;

const duplicatesCheck = (
  postGroup: PostGroupsType,
  severity: ErrorMessageSeverity,
  type: 'TwitterSocialProfile' | 'PinterestSocialProfile',
  maxProfilesCount: number,
  validationMessage: string
): PostEditorPostErrors => {
  const { profiles, customizeSchedulePerProfile, customizeContentPerProfile } = postGroup;
  const selectedSocialProfiles = profiles.filter(p => p.__typename === type);
  const selectedProfilesIds = selectedSocialProfiles.map(p => p.profileId);
  if (selectedSocialProfiles.length <= maxProfilesCount) {
    return false;
  }
  if (customizeContentPerProfile || customizeSchedulePerProfile) {
    let results: PostEditorPostError[] = [];
    const filteredPosts = postGroup.posts.filter(
      post => post.customizedProfileId && selectedProfilesIds.includes(post.customizedProfileId)
    );
    const postTimings = filteredPosts.map(p => p.scheduling.postTiming);
    const samePostTimingsCounter = postTimings.reduce(
      (prev: SamePostTimerObject, current) => {
        return { ...prev, [current]: prev[current] + 1 };
      },
      { QUEUE: 0, RECURRING: 0, SCHEDULE: 0, NOW: 0 }
    );

    const entries: [keyof typeof PostTiming, number][] = Object.entries(samePostTimingsCounter).filter(
      (entry): entry is [keyof typeof PostTiming, number] => {
        const [key] = entry;
        return isPostTimingKeyGuard(key);
      }
    );

    let maxKey = entries[0][0];
    let maxValue = entries[0][1];

    for (const [key, value] of entries) {
      if (value > maxValue) {
        maxValue = value;
        maxKey = key;
      }
    }
    const hasMoreThanAllowedSameTimings = maxValue > maxProfilesCount;
    if (!hasMoreThanAllowedSameTimings) return false;
    const nextVariations: TextVariations[] = postGroup.posts.flatMap((p, postIndex) => {
      const isPn = p.customizedProfileId && selectedProfilesIds.includes(p.customizedProfileId);
      if (!isPn) return [];
      const { texts: variations, variationMeta } = p.content;
      const { textVariationIndex: index, currentVariationIndex } = variationMeta;
      const vIndex = typeof index === 'number' ? index : currentVariationIndex;
      const texts = variations.map(v => v.text);
      const nextVariation = texts[vIndex - 1];
      return { text: serializeText(nextVariation), postIndex };
    });

    const initObject: SameVariationsObject = {};

    // generate an object which holds the count
    const sameVariationsObject: SameVariationsObject = nextVariations.reduce((prev, current) => {
      // define the property if not defined
      prev[current['text']] = prev[current['text']] || { value: 0, postIndex: [] };
      // increment the count
      prev[current['text']].value++;
      prev[current['text']].postIndex = [...prev[current['text']].postIndex, current.postIndex];
      // return the object reference
      return prev;
      // set initial value as an empty object
    }, initObject);

    const variationEntries = Object.entries<SameVariationsObjectValue>(sameVariationsObject);

    let maxVariationKey = variationEntries[0][0];
    let maxVariationValue = variationEntries[0][1].value;

    for (const [key, value] of variationEntries) {
      if (value.value > maxVariationValue) {
        maxVariationValue = value.value;
        maxVariationKey = key;
      }
    }

    const hasMoreThanAllowedSameVariations = maxVariationValue > maxProfilesCount;
    if (!hasMoreThanAllowedSameVariations) return false;

    let hasMoreThanAllowedSameScheduledDates = false;
    let maxDateTimeKey = '';
    let maxDateTimeValue = -Infinity;
    let scheduleTimings: DateTimeTimings[] = [];
    if (maxKey === 'SCHEDULE') {
      // this means that we have more than 2 same scheduled posts, so we should check is the next timings are the same
      scheduleTimings = postGroup.posts.flatMap((post, postIndex) => {
        const isScheduled = post.scheduling.postTiming === 'SCHEDULE';
        const isPn = post.customizedProfileId && selectedProfilesIds.includes(post.customizedProfileId);
        if (!isScheduled || !isPn) return [];
        const scheduledTimes = post.scheduling.scheduledLocalDateTimes.filter(d => !d.isPast);
        if (!scheduledTimes.length) return [];
        return { dateTime: scheduledTimes[0].dateTime, postIndex };
      });
      const initDateTimeObject: SameVariationsObject = {};

      // generate an object which holds the count
      const sameScheduledTimesObject: SameVariationsObject = scheduleTimings.reduce((prev, current) => {
        // define the property if not defined
        prev[current['dateTime']] = prev[current['dateTime']] || { value: 0, postIndex: [] };
        // increment the count
        prev[current['dateTime']].value++;
        prev[current['dateTime']].postIndex = [...prev[current['dateTime']].postIndex, current.postIndex];
        // return the object reference
        return prev;
        // set initial value as an empty object
      }, initDateTimeObject);

      const dateTimeEntries = Object.entries<SameVariationsObjectValue>(sameScheduledTimesObject);

      // we still don't have any scheduled dates initialized so don't validate yet, or don't say it's invalid till w eget them
      if (!dateTimeEntries.length) return false;

      maxDateTimeKey = dateTimeEntries[0][0];
      maxDateTimeValue = dateTimeEntries[0][1].value;

      for (const [key, value] of dateTimeEntries) {
        if (value.value > maxDateTimeValue) {
          maxDateTimeValue = value.value;
          maxDateTimeKey = key;
        }
      }

      hasMoreThanAllowedSameScheduledDates = maxDateTimeValue > maxProfilesCount;
      if (!hasMoreThanAllowedSameScheduledDates) return false;
    }

    // we have both moreThanAllowed same timings and variations, so we need to check if they correspond to the same post and set the errors if they do
    // also, we might have hasMoreThanAllowedSameScheduledDates if we have scheduled timing turned on.

    const initialVariationPostIndexes: number[] = [];
    const nextVariationsPostIndexes = nextVariations.reduce(
      (prev, current) => (current.text === maxVariationKey ? [...prev, current.postIndex] : prev),
      initialVariationPostIndexes
    );
    const initialTimingPostIndexes: number[] = [];
    const postTimingPostIndexes =
      maxKey === 'SCHEDULE'
        ? scheduleTimings.reduce(
            (prev, current) => (current.dateTime === maxDateTimeKey ? [...prev, current.postIndex] : prev),
            initialTimingPostIndexes
          )
        : postGroup.posts.flatMap((post, postIndex) => (post.scheduling.postTiming !== maxKey ? [] : postIndex));

    const initialIntersectionValue: number[] = [];
    const postIndexesIntersection = nextVariationsPostIndexes.reduce((prev, current) => {
      return prev.includes(current) || !postTimingPostIndexes.includes(current) ? prev : [...prev, current];
    }, initialIntersectionValue);

    if (postIndexesIntersection.length <= maxProfilesCount) return false;

    postGroup.posts.forEach((post, index) => {
      if (postIndexesIntersection.includes(index) && postIndexesIntersection.length > maxProfilesCount) {
        const selectedProfile = selectedSocialProfiles.find(p => p.profileId === post.customizedProfileId);
        results = [
          ...results,
          {
            profileId: post.customizedProfileId,
            variationId: null,
            severity: severityResolver(post, severity),
            selectedSocialProfiles: selectedProfile ? [selectedProfile] : [],
            message: validationMessage
          }
        ];
      }
    });

    return results.length ? results : false;
  } else {
    // we are not customizing, just check postGroup
    return selectedSocialProfiles.length <= maxProfilesCount
      ? false
      : {
          profileId: null,
          variationId: null,
          severity: severityResolver(postGroup.groupPostAttributes, severity),
          selectedSocialProfiles: selectedSocialProfiles,
          message: validationMessage
        };
  }
};

export const pinterestDuplicatesCheck = (
  postGroup: PostGroupsType,
  severity: ErrorMessageSeverity
): PostEditorPostErrors =>
  duplicatesCheck(
    postGroup,
    severity,
    'PinterestSocialProfile',
    3,
    PostEditorValidationMessages.pinterestDuplicatesCheck
  );

export const preventTwDuplicate = (postGroup: PostGroupsType, severity: ErrorMessageSeverity): PostEditorPostErrors =>
  duplicatesCheck(postGroup, severity, 'TwitterSocialProfile', 1, PostEditorValidationMessages.preventTwDuplicate);
